import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { apiClient } from 'src/libs/axios'
import { Announcement, AnnouncementFilters } from 'src/types/announcement'

export interface PublicAnnouncementFilters extends Omit<AnnouncementFilters, 'status'> {
    page?: number
    pageSize?: number
}

export interface PublishedAnnouncementsResponse {
    announcements: Announcement[]
    total: number
    page: number
    pageSize: number
}

export const announcementKeys = {
    all: ['announcements'] as const,
    published: () => [...announcementKeys.all, 'published'] as const,
    publishedWithFilters: (filters: PublicAnnouncementFilters) => [...announcementKeys.published(), filters] as const,
}

const getPublishedAnnouncements = async (filters?: PublicAnnouncementFilters): Promise<PublishedAnnouncementsResponse> => {
    const params = new URLSearchParams()

    if (filters?.category) params.append('category', filters.category)

    // Convert page/pageSize to limit/offset like the admin service
    // Set defaults to ensure proper pagination
    const pageSize = filters?.pageSize || 10
    const page = filters?.page || 1

    params.append('limit', pageSize.toString())

    const offset = (page - 1) * pageSize
    params.append('offset', offset.toString())

    const queryString = params.toString()
    const url = `/v1/public/announcements${queryString ? `?${queryString}` : ''}`

    const response = await apiClient.get<{ announcements: Announcement[], count: number }>(url)

    // Transform the response to match expected format
    return {
        announcements: response.data.announcements,
        total: response.data.count,
        page: page,
        pageSize: pageSize
    }
}

export const useGetPublishedAnnouncements = <TData = PublishedAnnouncementsResponse, TError = Error>(
    filters?: PublicAnnouncementFilters,
    options?: Omit<UseQueryOptions<PublishedAnnouncementsResponse, TError, TData>, 'queryKey' | 'queryFn'>
) => {
    return useQuery({
        queryKey: announcementKeys.publishedWithFilters(filters || {}),
        queryFn: () => getPublishedAnnouncements(filters),
        staleTime: 2 * 60 * 1000, // 2 minutes cache
        ...options,
    })
} 